import axios from 'axios';
import JSDB from 'fromKotlin/nk';

/** Self-hosted OpenRouteService / VROOM (same host as NKAdapter.getOptimizedRoutes2). */
const ORS_BASE_URL = 'https://ai.solvitcompany.com:8400';
const ROUTE_OPTIMIZATION_URL = `${ORS_BASE_URL}/optimization`;
/** This instance only has driving-car — driving-hgv returns 400. */
const ORS_DIRECTIONS_URL = `${ORS_BASE_URL}/ors/v2/directions/driving-car`;

/** Same geocoder as NKAdapter.getGeoName */
const GEOCODE_URL = 'https://graphhopper.com/api/1/geocode';
const GEOCODE_KEY = 'f816fb13-e4dc-4565-965a-072f6d940a9c';

export type GeoPoint = { lat: number; lon: number };

/** Geocodes a free-text address (used for per-order static_data forced addresses). */
export const geocodeAddress = async (address: string): Promise<GeoPoint | null> => {
    try {
        const res = await axios.get(GEOCODE_URL, {params: {q: address, locale: 'il', key: GEOCODE_KEY}});
        const point = res.data?.hits?.[0]?.point;
        if (point?.lat != null && point?.lng != null) return {lat: point.lat, lon: point.lng};
        return null;
    } catch (e) {
        return null;
    }
};

export const parseLocationString = (loc: string): GeoPoint | null => {
    const parts = loc.split(',');
    if (parts.length < 2) return null;
    const lat = Number(parts[0]);
    const lon = Number(parts[1]);
    if (Number.isNaN(lat) || Number.isNaN(lon)) return null;
    return { lat, lon };
};

export interface RouteOptimizationJob {
    id: number;
    location: number[];
    time_windows?: number[][];
    service?: number;
    priority?: number;
    amount?: number[];
    skills?: number[];
}

export interface RouteOptimizationVehicle {
    id: number;
    profile?: string;
    start?: number[];
    end?: number[];
    time_window?: number[];
    capacity?: number[];
    service?: number;
    skills?: number[];
}

export interface RouteOptimizationRequest {
    jobs: RouteOptimizationJob[];
    vehicles: RouteOptimizationVehicle[];
}

export interface RouteOptimizationStep {
    job?: number;
    arrival?: number;
    type?: string;
}

export interface RouteOptimizationRoute {
    duration: number;
    steps: RouteOptimizationStep[];
}

export interface RouteOptimizationResponse {
    routes: RouteOptimizationRoute[];
}

export const optimizeRoute = async (request: RouteOptimizationRequest): Promise<RouteOptimizationResponse> => {
    const response = await axios.post<RouteOptimizationResponse>(ROUTE_OPTIMIZATION_URL, request, {
        headers: { 'Content-Type': 'application/json' }
    });
    return response.data;
};

export interface DrivingDirectionsSegment {
    duration: number;
    distance?: number;
}

export interface DrivingDirectionsRoute {
    geometry: string;
    segments: DrivingDirectionsSegment[];
    summary: { duration: number; distance?: number };
}

export interface DrivingDirectionsResponse {
    routes: DrivingDirectionsRoute[];
}

/** Road geometry + per-leg times from self-hosted ORS (not api.openrouteservice.org). */
export const getDrivingDirections = async (coordinates: number[][]): Promise<DrivingDirectionsResponse> => {
    const response = await axios.post<DrivingDirectionsResponse>(
        ORS_DIRECTIONS_URL,
        { coordinates, geometry: true, instructions: true },
        { headers: { 'Content-Type': 'application/json' } }
    );
    return response.data;
};

/** Builds the VROOM request payload matching NKAdapter.getOptimizedRoutes2 */
export const buildNkOptimizationRequest = (
    locations: GeoPoint[],
    start?: GeoPoint | null,
    end?: GeoPoint | null
): RouteOptimizationRequest => {
    const vehicle: RouteOptimizationVehicle = {
        id: 1,
        profile: 'driving-car',
        capacity: [1000],
        skills: [1],
    };
    if (start) vehicle.start = [start.lon, start.lat];
    if (end) vehicle.end = [end.lon, end.lat];

    const jobs: RouteOptimizationJob[] = locations.map((loc, index) => ({
        id: index + 1,
        service: 300,
        amount: [1],
        location: [loc.lon, loc.lat],
        skills: [1],
    }));

    return { jobs, vehicles: [vehicle] };
};

/** Parses optimized job order from the VROOM response, matching NKAdapter.getOptimizedRoutes2 */
export const parseNkOptimizationJobOrder = (
    response: RouteOptimizationResponse,
    hasStart: boolean,
    hasEnd: boolean
): number[] => {
    const steps = response.routes?.[0]?.steps ?? [];
    const from = hasStart ? 1 : 0;
    const to = hasEnd ? steps.length - 1 : steps.length;
    return steps
        .slice(from, to)
        .map((step) => step.job)
        .filter((job): job is number => job != null);
};

/** Optimizes geographic locations using the AI host :8400 API (NKAdapter.getOptimizedRoutes2) */
export const optimizeLocations = async (
    locations: GeoPoint[],
    start?: GeoPoint | null,
    end?: GeoPoint | null
): Promise<number[]> => {
    if (!locations.length) return [];

    const request = buildNkOptimizationRequest(locations, start, end);
    const response = await optimizeRoute(request);
    let jobIndices = parseNkOptimizationJobOrder(response, !!start, !!end);
    if (start) {
        jobIndices = jobIndices.map((id) => id - 1);
    }
    return jobIndices;
};

export interface OptimizedClientRows {
    /** Client ids in optimized order (may repeat if the same client had multiple selected rows). */
    orderedClientIds: number[];
    /** Original indices into the input clientIds/forcedAddresses arrays, in optimized order. Use this
     *  — instead of the ids — to map results back to specific rows whenever the same client id can
     *  appear more than once (e.g. a client with its normal order plus a one-off order carrying a
     *  forced/כתובת מאולצת address): keying by client id would collide the two rows together. */
    orderedIndices: number[];
}

/**
 * Optimizes client visit order by ID, mirroring NKDBInterface.getOptimizedRoute + NKAdapter.getOptimizedRoutes2.
 * Returns client IDs in optimized order; clients without coordinates are appended at the end.
 */
export const optimizeClientIds = async (
    clientIds: number[],
    options?: {
        startPoint?: string;
        endPoint?: string;
        /** Parallel to clientIds (matched by index, NOT by client id): a per-row forced (static_data)
         *  address that replaces that specific row's client location. Keying this by client id would be
         *  wrong whenever the same client has more than one row here (e.g. a regular order plus a
         *  one-off order with a forced/כתובת מאולצת address) — each row needs its own address resolved
         *  independently, same as NKDBInterface.getOptimizedRoute's `forcedAddresses` list. */
        forcedAddresses?: (string | undefined)[];
    }
): Promise<OptimizedClientRows> => {
    const clientsWithLocation: { clientIndex: number; point: GeoPoint }[] = [];
    const clientsWithoutLocation: number[] = [];

    // Geocode forced addresses first (per row index); on failure fall back to the regular client location.
    const forcedPoints: Record<number, GeoPoint> = {};
    if (options?.forcedAddresses) {
        await Promise.all(options.forcedAddresses.map(async (address, index) => {
            if (!address) return;
            const point = await geocodeAddress(address);
            if (point) forcedPoints[index] = point;
        }));
    }

    clientIds.forEach((id, index) => {
        const client = JSDB().getClient(Number(id))?.first;
        const point = forcedPoints[index] ?? (client?.location ? parseLocationString(client.location) : null);
        if (!point) {
            clientsWithoutLocation.push(index);
        } else {
            clientsWithLocation.push({ clientIndex: index, point });
        }
    });

    if (!clientsWithLocation.length) {
        return {
            orderedClientIds: clientIds,
            orderedIndices: clientIds.map((_, index) => index),
        };
    }

    const start = options?.startPoint ? parseLocationString(options.startPoint) : null;
    const end = options?.endPoint ? parseLocationString(options.endPoint) : null;
    const optimizedIndices = await optimizeLocations(
        clientsWithLocation.map((entry) => entry.point),
        start,
        end
    );

    const orderedIndices = optimizedIndices.map((i) => clientsWithLocation[i].clientIndex);
    orderedIndices.push(...clientsWithoutLocation);
    return {
        orderedClientIds: orderedIndices.map((index) => clientIds[index]),
        orderedIndices,
    };
};
